import { Link } from "react-router-dom";
import { useMe, canUseTool } from "../lib/me";

interface Tool {
  key: string;
  label: string;
  description: string;
  path: string;
  ported: boolean;
  accent: string;
}

const TOOLS: Tool[] = [
  {
    key: "collection",
    label: "Payment Collection",
    description: "Track open invoices, send bilingual payment reminders (TH/EN), maintain client credit terms.",
    path: "/collection/dashboard",
    ported: true,
    accent: "from-indigo-50 to-white",
  },
  {
    key: "payroll",
    label: "Payroll",
    description: "Monthly salary processing, SSO, PND.50, payslip generation.",
    path: "/payroll",
    ported: true,
    accent: "from-emerald-50 to-white",
  },
  {
    key: "wht",
    label: "WHT Monitor",
    description: "Withholding-tax tracking, vendor certificates, PND.3/53 prep.",
    path: "/wht",
    ported: true,
    accent: "from-amber-50 to-white",
  },
  {
    key: "monday",
    label: "Contacts",
    description: "Internal CRM / boards — Monday-style workflow management.",
    path: "/monday",
    ported: true,
    accent: "from-sky-50 to-white",
  },
  {
    key: "outreach",
    label: "Outreach",
    description: "Cold-outreach pipeline — curate MWC contacts, verify emails, send personalised campaigns.",
    path: "/outreach",
    ported: true,
    accent: "from-rose-50 to-white",
  },
];

export default function Home() {
  const me = useMe();
  const firstName = (me?.name || "").split(" ")[0];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">
          {firstName ? `Hi, ${firstName}` : "MailBIT internal tools"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {me?.allowed_tools === "all"
            ? "You have access to all tools."
            : me?.allowed_tools && me.allowed_tools.length === 0
              ? "No tools have been granted to your account yet — ask an admin."
              : "Pick a tool to get started."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {TOOLS.map((t) => {
          const allowed = canUseTool(me, t.key);
          return <ToolCard key={t.key} tool={t} allowed={allowed} />;
        })}
      </div>
    </div>
  );
}

function ToolCard({ tool, allowed }: { tool: Tool; allowed: boolean }) {
  const usable = tool.ported && allowed;
  const card = (
    <div className={`card card-hover relative h-full overflow-hidden p-6 ${usable ? "" : "opacity-70"}`}>
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tool.accent} opacity-70`} />
      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="text-lg font-semibold tracking-tightish text-slate-900">{tool.label}</div>
          {!tool.ported && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200">
              Coming soon
            </span>
          )}
          {tool.ported && !allowed && (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-600 ring-1 ring-rose-100">
              No access
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-slate-600">{tool.description}</p>
        {usable && (
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-700">
            Open
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" /></svg>
          </div>
        )}
      </div>
    </div>
  );
  return usable ? <Link to={tool.path}>{card}</Link> : card;
}
